/**
 * Utilidades para cuenta corriente de cliente.
 * Fuente: GET /ventas/cuentas-por-cobrar?cliente_id= + GET /clientes/:id/saldo
 */
import {
  calcularDiasAtraso,
  normalizarDeudaVencidaDias,
  parseCuentasPorCobrarResponse
} from './dashboardDeuda';

export function normalizeClienteId(id) {
  if (id === null || id === undefined) return '';
  return String(id).trim();
}

function parsePagoVenta(v) {
  const total = parseFloat(v?.total || 0);
  const pagado = parseFloat(v?.total_pagado ?? v?.monto_pagado ?? 0);
  return {
    total: Number.isFinite(total) ? total : 0,
    pagado: Number.isFinite(pagado) ? pagado : 0
  };
}

function normalizeEstadoPago(v) {
  return String(v?.estado_pago || '').toLowerCase().trim();
}

function isVentaCanceladaLocal(v) {
  const estado = String(v?.estado || '').toLowerCase();
  return estado === 'cancelada' || estado === 'anulada';
}

function isEstadoPagoCancelado(v) {
  const ep = normalizeEstadoPago(v);
  return ep === 'cancelado' || ep === 'cancelada';
}

function isEstadoPagoPagado(v) {
  const ep = normalizeEstadoPago(v);
  return ep === 'pagado' || ep === 'completada';
}

/** Misma prioridad que resolveSaldoPendiente (backend). Ignora saldo_pendiente stale. */
export function resolveSaldoVenta(v) {
  if (isVentaCanceladaLocal(v) || isEstadoPagoCancelado(v)) return 0;
  if (isEstadoPagoPagado(v)) return 0;

  const { total, pagado } = parsePagoVenta(v);
  if (total > 0 && pagado >= total) return 0;

  if (total > 0) {
    const calculado = Math.max(0, total - pagado);
    if (calculado > 0) return calculado;
    return 0;
  }

  const saldoCampo = parseFloat(v?.saldo_pendiente);
  if (Number.isFinite(saldoCampo) && saldoCampo > 0) return saldoCampo;
  return 0;
}

export function extractVentasCliente(cuentasRaw, clienteId) {
  const cid = normalizeClienteId(clienteId);
  const cuentas = parseCuentasPorCobrarResponse(cuentasRaw);
  const porCliente = Array.isArray(cuentas.por_cliente) ? cuentas.por_cliente : [];
  const row = porCliente.find((r) => normalizeClienteId(r.cliente_id) === cid);
  if (row && Array.isArray(row.ventas) && row.ventas.length > 0) {
    return row.ventas;
  }
  const ventas = Array.isArray(cuentas.ventas) ? cuentas.ventas : [];
  return ventas.filter(
    (v) => normalizeClienteId(v.cliente_id) === cid && resolveSaldoVenta(v) > 0
  );
}

export function buildFilasCuentaCorriente(ventas, deudaVencidaDias = 30) {
  const diasCriterio = normalizarDeudaVencidaDias(deudaVencidaDias);

  return (ventas || [])
    .filter((v) => {
      const saldo = resolveSaldoVenta(v);
      const estado = String(v.estado || '').toLowerCase();
      return saldo > 0 && estado !== 'cancelada' && estado !== 'anulada';
    })
    .map((v) => {
      const saldo = resolveSaldoVenta(v);
      const total = parseFloat(v.total) || 0;
      const pagado = parseFloat(v.total_pagado ?? v.monto_pagado ?? 0);
      const diasAtraso = calcularDiasAtraso(v.fecha || v.fechaCreacion);
      return {
        id: v.id,
        numero: v.numero || v.numero_venta || `V-${String(v.id || '').slice(-6).toUpperCase()}`,
        fecha: v.fecha || v.fechaCreacion || null,
        total,
        pagado,
        saldo,
        diasAtraso,
        vencida: diasAtraso >= diasCriterio,
        metodoPago: v.metodo_pago || v.forma_pago || v.medio_pago || null,
        estado: v.estado_pago || v.estado || null,
        raw: v
      };
    })
    .sort((a, b) => new Date(a.fecha || 0) - new Date(b.fecha || 0));
}

/**
 * Tarjetas y tabla usan la misma fuente: suma de filas (ventas adeudadas).
 * saldoBackend solo se expone para detectar desalineación, no para la card.
 */
/** Prioriza venta vencida con mayor atraso y saldo (reutilizado CRM + ficha cliente). */
export function elegirFilaCuentaParaPago(filasCuenta) {
  if (!filasCuenta?.length) return null;
  return [...filasCuenta].sort((a, b) => {
    if (a.vencida !== b.vencida) return a.vencida ? -1 : 1;
    const diasDiff = (b.diasAtraso ?? 0) - (a.diasAtraso ?? 0);
    if (diasDiff !== 0) return diasDiff;
    return (b.saldo ?? 0) - (a.saldo ?? 0);
  })[0];
}

export function resumirCuentaCorriente(filas, deudaVencidaDias, saldoBackend = null) {
  const diasCriterio = normalizarDeudaVencidaDias(deudaVencidaDias);
  const sumaFilas = (filas || []).reduce((sum, f) => sum + (parseFloat(f.saldo) || 0), 0);
  const backend = saldoBackend != null ? parseFloat(saldoBackend) || 0 : null;
  const montoVencido = (filas || [])
    .filter((f) => f.vencida)
    .reduce((sum, f) => sum + (parseFloat(f.saldo) || 0), 0);

  return {
    diasCriterio,
    totalDeuda: sumaFilas,
    montoVencido,
    cantidadVentas: (filas || []).length,
    sumaFilas,
    saldoBackend: backend,
    desalineadoBackend: backend != null && Math.abs(backend - sumaFilas) >= 0.02
  };
}
